import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, OnDestroy, signal, Signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { SettingsService } from '@app/_services/settings.service';
import { CacheService } from '@app/_services/cache.service';
import { PublicNameService } from '@app/_services/public-name.service';
import { NotificationComponent } from '@app/components/common/notifications/notification/notification.component';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { MeUserAvatarComponent } from '@app/components/common/account/me-user-avatar/me-user-avatar.component';
import { MessageViewComponent } from '@app/components/addons/message/message-view/message-view.component';
import { MessageCollaboratorPickerComponent } from '@app/components/addons/message/message-collaborator-picker/message-collaborator-picker.component';
import { SmartTimeDiffPipe } from '@app/_pipes/smart-time-diff.pipe';
import { MobileBottomBarComponent } from '@app/components/shared/mobile-bottom-bar/mobile-bottom-bar.component';
import { MessageStore } from '@app/_stores';
import { UserResourceStore } from '@app/_stores/user-resource.store';
import { MessageSetup } from '@app/_interfaces/message-setup.interface';
import { MembersModel, CollaboratorEntry } from '@app/_interfaces/generic.interface';
import { ModalService } from '@app/_services/modal.service';
import { getNewId } from '@app/_utils/utils';
import { MessageDisplayService } from '@app/_services/message-display.service';
import { log } from '@app/_services/logger';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-message-list',
    imports: [
        CommonModule,
        SvgComponent,
        NotificationComponent,
        NoResultBlockComponent,
        MeUserAvatarComponent,
        MessageViewComponent,
        SmartTimeDiffPipe,
        MobileBottomBarComponent,
    ],
    templateUrl: './message-list.component.html',
    styleUrl: './message-list.component.scss'
})
export class MessageListComponent implements OnInit, OnDestroy {
    readonly messageStore = inject(MessageStore);
    readonly mds = inject(MessageDisplayService);
    readonly userResourceStore = inject(UserResourceStore);
    readonly publicNameService = inject(PublicNameService);
    readonly ss = inject(SettingsService);
    readonly cc = inject(CacheService);
    private readonly dialog = inject(MatDialog);
    private readonly modalService = inject(ModalService);

    private readonly unSubscribe = new Subject<void>();

    /**
     * Currently selected setup ID for master-detail view.
     * When set, the message-view is shown; when null, the list is shown.
     */
    selectedSetupId: WritableSignal<string | null> = signal(null);

    /**
     * The currently selected setup object (for passing entityId/entityCollection
     * to the message view).
     */
    selectedSetup: Signal<MessageSetup | null> = computed(() => {
        const id = this.selectedSetupId();
        if (!id) return null;
        return this.sortedSetups().find(s => s.id === id) ?? null;
    });

    /**
     * Sorted, non-deleted message setups from the store.
     */
    sortedSetups: Signal<MessageSetup[]> = computed(() => {
        return this.messageStore.sortedSetups();
    });

    trackBySetupId(_index: number, setup: MessageSetup): string {
        return setup.id;
    }

    async ngOnInit(): Promise<void> {
        log.info('MessageListComponent', 'Initializing message list');

        // Load cached setups from IndexedDB (belt-and-suspenders — store hook also subscribes)
        await this.messageStore.loadCachedSetups();

        // Preload display names for all members across all setups
        this.preloadMemberNames();
    }

    /**
     * Preload display names for members in all setups.
     * Uses PublicNameService.preload() with MembersModel for each setup.
     */
    private async preloadMemberNames(): Promise<void> {
        const setups = this.sortedSetups();
        if (setups.length === 0) return;

        for (const setup of setups) {
            if (setup.members) {
                // Preload via MembersModel (handles ownerEmail + collaborator emails + UIDs)
                await this.publicNameService.preload(setup.members);
            }
        }

        log.debug('MessageListComponent', `Preloaded names for ${setups.length} setups`);
    }

    /**
     * Derive the message name based on the setup type (same logic as Flutter):
     *   entityId != null            -> entity message, show entity collection name
     *   entityId == null, 2 members -> direct message, show other person's name
     *   entityId == null, 3+ members -> group message, show name or derived from member names
     */
    // Title, subtitle, avatar, lastMessage logic all moved to MessageDisplayService (mds)

    /**
     * Handle click on a message tile.
     * Narrow screen: master-detail swap (full screen, matching Flutter mobile).
     * Big screen: open as modal (matching Flutter big screen / ShowMeScreen openAsModal).
     */
    onMessageTileClick(setup: MessageSetup): void {
        log.info('MessageListComponent', 'Message tile clicked, opening message view', { setupId: setup.id });
        this.openMessageView(setup.id);
    }

    /**
     * Open a message view as a side panel overlay.
     * Mobile (<=750px): full screen panel.
     * Desktop (>750px): 622px right-side panel (same pattern as meo-page).
     */
    private openMessageView(setupId: string): void {
        this.selectedSetupId.set(setupId);
    }

    /**
     * Return from message view back to the list.
     */
    goBackToList(): void {
        log.debug('MessageListComponent', 'Returning to message list');
        this.selectedSetupId.set(null);
    }

    /**
     * Check if a setup has unread messages.
     */
    isUnread(setup: MessageSetup): boolean {
        return this.userResourceStore.isUnread(setup.id, setup.lastMessageAt);
    }

    /**
     * Check if a setup is muted.
     */
    isMuted(setup: MessageSetup): boolean {
        return this.userResourceStore.isMuted(setup.id);
    }

    /**
     * Open the new message collaborator picker dialog.
     */
    openNewMessagePicker(): void {

        const dialogRef =  this.modalService.openLevel1(MessageCollaboratorPickerComponent)

        this.modalService.getClosedObservable(dialogRef).subscribe(async (selectedEmails: string[] | undefined) => {
            if (!selectedEmails || selectedEmails.length === 0) return;
            await this.createMessageFromSelection(selectedEmails);
        });
    }

    // ============================================================
    // Private: Message creation
    // ============================================================

    /**
     * Create a new message from selected emails.
     * For direct (1 selected): check if a direct conversation already exists.
     * For group: create a new group conversation with derived name.
     */
    private async createMessageFromSelection(selectedEmails: string[]): Promise<void> {
        const currentUserEmail = this.cc.user?.email?.toLowerCase();
        if (!currentUserEmail) return;

        const isDirect = selectedEmails.length === 1;

        // For direct conversations, check if one already exists
        if (isDirect) {
            const targetEmail = selectedEmails[0].toLowerCase();
            const existingSetup = this.findExistingDirectConversation(targetEmail);
            if (existingSetup) {
                log.info('MessageListComponent', 'Direct conversation already exists, opening', { setupId: existingSetup.id });
                this.openMessageView(existingSetup.id);
                return;
            }
        }

        // Build collaborators map
        const collaborators: { [email: string]: CollaboratorEntry } = {};
        for (const email of selectedEmails) {
            const emailLower = email.toLowerCase();
            collaborators[emailLower] = {
                uid: null,
                role: 'editor',
                addedAt: new Date(),
            };
        }

        const members: MembersModel = {
            ownerEmail: currentUserEmail,
            memberUids: [],
            collaborators,
        };

        // name is null — title is always dynamically computed from members.
        // Only set via explicit rename from settings.
        const newSetup: MessageSetup = {
            id: getNewId(),
            uid: this.cc.user.uid,
            entityId: null,
            entityCollection: null,
            name: null,
            lastMessageText: null,
            lastMessageUid: null,
            lastMessageAt: null,
            lastUpdatedBy: null,
            parentDeletedAt: null,
            members,
            createdAt: new Date(),
            localUpdatedAt: new Date(),
        };

        log.info('MessageListComponent', 'Creating new conversation', {
            setupId: newSetup.id,
            isDirect,
            memberCount: selectedEmails.length,
        });

        await this.messageStore.addSetup(newSetup);

        // Also save each selected email as a collaborator suggestion in userResources
        for (const email of selectedEmails) {
            this.userResourceStore.addCollaboratorEmail(email);
        }

        // Open the new conversation
        this.openMessageView(newSetup.id);
    }

    /**
     * Find an existing direct conversation with a specific email.
     * Direct = 2 members (owner + 1 collaborator), no entityId.
     */
    private findExistingDirectConversation(targetEmail: string): MessageSetup | undefined {
        const currentUserEmail = this.cc.user?.email?.toLowerCase();
        const setups = this.sortedSetups();

        return setups.find(setup => {
            // Must not be an entity conversation
            if (setup.entityId) return false;
            if (!setup.members) return false;

            // Must have exactly 1 collaborator (owner + 1 = 2 members)
            const collabEmails = Object.keys(setup.members.collaborators || {});
            if (collabEmails.length !== 1) return false;

            // Owner must be current user or target
            const ownerEmail = setup.members.ownerEmail?.toLowerCase();
            const collabEmail = collabEmails[0].toLowerCase();

            // Check if the two members are current user + target
            const memberSet = new Set([ownerEmail, collabEmail]);
            return memberSet.has(currentUserEmail) && memberSet.has(targetEmail.toLowerCase());
        });
    }

    ngOnDestroy(): void {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }
}
